/**
 * Businesses data fetching hooks.
 * 
 * @module hooks/useBusinesses
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { BusinessWithOwner, PaginatedResponse, BusinessQueryOptions } from '../types';

/**
 * Extended business type with last analysis data.
 * @see CSDEV-146 - Businesses list: add last analysis + AI score columns
 */
export interface BusinessWithOwnerExtended extends BusinessWithOwner {
  last_analysis_date?: string | null;
  latest_ai_score?: number | null;
}

/**
 * Fetches paginated businesses list with owner information.
 * Includes last analysis date and AI score for each business.
 * 
 * @param options - Query options
 * @returns Query result with paginated businesses
 * @see CSDEV-146 - Businesses list: add last analysis + AI score columns and search by owner email
 */
export function useBusinesses(options: BusinessQueryOptions = {}) {
  const {
    page = 1,
    pageSize = 25,
    search,
    ownerId,
    industry,
    ownerEmail,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = options;

  return useQuery({
    queryKey: ['businesses', options],
    queryFn: async (): Promise<PaginatedResponse<BusinessWithOwnerExtended>> => {
      // Build base query with users join
      let query = supabase
        .from('businesses')
        .select('*, users!businesses_profile_id_fkey(id, email, full_name)', { count: 'exact' });

      // Apply search filter for business name
      if (search) {
        query = query.ilike('business_name', `%${search}%`);
      }

      // Apply owner filter by ID
      if (ownerId) {
        query = query.eq('profile_id', ownerId);
      }

      // Apply industry filter
      if (industry) {
        query = query.eq('industry', industry);
      }

      // Apply owner email filter by resolving matching user IDs first
      if (ownerEmail) {
        const { data: owners, error: ownersError } = await supabase
          .from('users')
          .select('id')
          .ilike('email', `%${ownerEmail}%`);

        if (ownersError) {
          throw new Error(`Failed to fetch owner emails: ${ownersError.message}`);
        }

        const ownerIds = owners?.map((owner: { id: string }) => owner.id) || [];
        if (ownerIds.length === 0) {
          return {
            data: [],
            count: 0,
            page,
            pageSize,
            totalPages: 0,
          };
        }

        query = query.in('profile_id', ownerIds);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to fetch businesses: ${error.message}`);
      }

      // Fetch latest reports for each business to get last analysis date and AI score.
      // Skip enrichment for bulk fetches (>500 IDs) to avoid exceeding PostgREST URL
      // length limits with large .in() clauses. Bulk fetches are used by the client-side
      // internal/external filter — those views don't display report columns.
      const businessIds = (data || []).map((b: BusinessWithOwner) => b.id);
      const reportsMap: Map<string, { created_at: string; avg_ai_visibility_score: number | null }> = new Map();

      if (businessIds.length > 0 && businessIds.length <= 500) {
        const { data: reports } = await supabase
          .from('full_reports')
          .select('business_id, created_at, avg_ai_visibility_score')
          .in('business_id', businessIds)
          .eq('status', 'completed')
          .order('created_at', { ascending: false });

        // Get the latest report for each business
        reports?.forEach((report: { business_id: string; created_at: string; avg_ai_visibility_score: number | null }) => {
          if (!reportsMap.has(report.business_id)) {
            reportsMap.set(report.business_id, {
              created_at: report.created_at,
              avg_ai_visibility_score: report.avg_ai_visibility_score,
            });
          }
        });
      }

      // Transform data to include last analysis info
      const transformedData: BusinessWithOwnerExtended[] = (data || []).map((business: BusinessWithOwner) => {
        const latestReport = reportsMap.get(business.id);
        return {
          ...business,
          last_analysis_date: latestReport?.created_at || null,
          latest_ai_score: latestReport?.avg_ai_visibility_score || null,
        };
      });

      return {
        data: transformedData,
        count: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
  });
}

/**
 * Fetches a single business by ID.
 * 
 * @param businessId - Business ID
 * @returns Query result with business details
 */
export function useBusiness(businessId: string | undefined) {
  return useQuery({
    queryKey: ['business', businessId],
    queryFn: async () => {
      if (!businessId) throw new Error('Business ID is required');

      const { data, error } = await supabase
        .from('businesses')
        .select('*, users!businesses_profile_id_fkey(id, email, full_name)')
        .eq('id', businessId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch business: ${error.message}`);
      }

      return data as BusinessWithOwner;
    },
    enabled: !!businessId,
  });
}

/**
 * Fetches unique industries for filter dropdown.
 * 
 * @returns Query result with industries
 */
export function useIndustries() {
  return useQuery({
    queryKey: ['industries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('businesses')
        .select('industry')
        .not('industry', 'is', null);

      if (error) {
        throw new Error(`Failed to fetch industries: ${error.message}`);
      }

      // Extract unique industries
      const industries = [...new Set(data?.map((b: { industry: string | null }) => b.industry).filter(Boolean))] as string[];
      return industries.sort();
    },
  });
}

/**
 * Fetches business reports.
 * 
 * @param businessId - Business ID
 * @returns Query result with business reports
 */
export function useBusinessReports(businessId: string | undefined) {
  return useQuery({
    queryKey: ['business-reports', businessId],
    queryFn: async () => {
      if (!businessId) throw new Error('Business ID is required');

      const { data, error } = await supabase
        .from('full_reports')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch business reports: ${error.message}`);
      }

      return (data || []) as Array<{
        id: string;
        business_id: string;
        profile_id: string;
        status: 'pending' | 'running' | 'completed' | 'failed';
        avg_ai_visibility_score: number | null;
        total_mentions: number | null;
        created_at: string;
        completed_at: string | null;
      }>;
    },
    enabled: !!businessId,
  });
}

/**
 * Fetches all businesses for filter dropdowns.
 * Returns a simplified list with id and name only.
 * 
 * @returns Query result with all businesses
 * @see CSDEV-143 - Reports page filter UI
 * @see CSDEV-144 - Content/FAQ/LLMs pages filter UI
 */
export function useAllBusinesses() {
  return useQuery({
    queryKey: ['all-businesses'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('businesses')
        .select('id, business_name')
        .order('business_name', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch businesses: ${error.message}`);
      }

      return (data || []) as Array<{ id: string; business_name: string }>;
    },
  });
}
